import { useUserActiveOrganization, useDeleteApp } from "@/api";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { toast } from "@/components/ui/use-toast";
import { DialogClose } from "@radix-ui/react-dialog";
import React, { Dispatch, SetStateAction } from "react";

type Props = {
  appName: string | undefined;
  appSlug: string | undefined;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const DeleteApp = ({ appName, appSlug, setOpen }: Props) => {
  const { activeOrganization } = useUserActiveOrganization();
  const [deleteApp, { loading: isDeletingApp }] = useDeleteApp();

  const deleteAppHandler = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      if (!activeOrganization || !appSlug) return;
      const { data: createAppData } = await deleteApp({
        variables: {
          appSlug,
          organizationSlug: activeOrganization?.slug
        }
      });
      if (!createAppData) return;
      if (createAppData.appDelete.__typename === "AppDeleted")
        toast({
          title: "App deleted",
          description: `${appName} was removed from Numerous.`
        });
      else if (
        createAppData.appDelete.__typename === "AppHasActiveSubscriptions"
      )
        toast({
          title: "Unable to delete app",
          description:
            "The app has active subscriptions, standing offers, or pending subscription offers. To delete this app, please cancel all subscriptions and offers first.",
          variant: "destructive",
          duration: 15000
        });
      setOpen(false);
    } catch (error) {
      toast({
        title: "App deletion failed",
        description:
          "Failed to delete the app. Please try again or report the issue."
      });
      console.error("An error occurred:", error);
    }
  };

  return (
    <Dialog.Root open onOpenChange={setOpen}>
      <Dialog.Content className="max-w-lg dark:bg-brand-gray-900">
        <Dialog.Header className="space-y-2 relative">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100 ">
            Delete app
          </Dialog.Title>
          <Dialog.Description className="text-brand-gray-700 dark:text-brand-gray-300">
            Are you sure you want to delete{" "}
            <span className="font-semibold">{appName}</span> and remove app from
            Numerous?
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer className="mt-8">
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={isDeletingApp}
            onClick={deleteAppHandler}
          >
            Delete
            {isDeletingApp && <LoadingCircle className="ml-2" />}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default DeleteApp;
